import React, { createContext, useContext, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "@/i18n/translations";
import { isEnglishPath, toLangPath } from "@/config/site";

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = isEnglishPath(location.pathname) ? "en" : "da";

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem("lotus-lang", lang);
    } catch (_) {
      /* ignore */
    }
  }, [lang]);

  const setLang = useCallback(
    (l) => {
      if (l === lang) return;
      const target = toLangPath(location.pathname, l);
      navigate(target + location.search + location.hash);
    },
    [lang, location, navigate]
  );

  const toggleLang = useCallback(() => setLang(lang === "da" ? "en" : "da"), [lang, setLang]);

  // Locale-aware path helper for non-<Link> navigation.
  const lp = useCallback(
    (path) =>
      lang === "en" && typeof path === "string" && path.startsWith("/")
        ? path === "/"
          ? "/en"
          : `/en${path}`
        : path,
    [lang]
  );

  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t, lp }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
